import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';

export interface ConciergeProperty {
  id: number;
  reference_number?: string;
  property_category: string;
  address: string;
  city: string;
  wilaya: string;
  owner_name: string;
  owner_phone?: string;
}

export interface ConciergeReservation {
  id?: number;
  property_id: number;
  guest_name: string;
  guest_phone?: string;
  start_date: string;
  end_date: string;
  price_per_night: number;
  nights_count?: number;
  total_cost?: number;
  amount_paid: number;
  balance_due?: number;
  notes?: string;
  is_pre_reservation?: boolean;
  confirmation_deadline?: string;
}

export interface ConciergeExpense {
  id?: number;
  property_id: number;
  expense_date: string;
  description: string;
  amount: number;
  reservation_id?: number;
  guest_name?: string;
}

export interface ExpensesResponse {
  expenses: ConciergeExpense[];
  total: number;
}

const getAgencyId = (): number => {
  const userStr = localStorage.getItem('user');
  if (!userStr) throw new Error('User not authenticated');
  const user = JSON.parse(userStr);
  if (!user.agency_id) throw new Error('Agency ID not found');
  return user.agency_id;
};

export const conciergeAPI = {
  getProperties: async (): Promise<ConciergeProperty[]> => {
    const token = localStorage.getItem('token');
    const agency_id = getAgencyId();
    const response = await axios.get(`${API_BASE_URL}/concierge/properties`, {
      params: { agency_id },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  },

  getReservations: async (propertyId: number, year: number, month: number): Promise<ConciergeReservation[]> => {
    const token = localStorage.getItem('token');
    const agency_id = getAgencyId();
    const response = await axios.get(`${API_BASE_URL}/concierge/reservations/${propertyId}/${year}/${month}`, {
      params: { agency_id },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  },

  createReservation: async (reservation: ConciergeReservation): Promise<ConciergeReservation> => {
    const token = localStorage.getItem('token');
    const agency_id = getAgencyId();
    const response = await axios.post(`${API_BASE_URL}/concierge/reservations`, {
      ...reservation,
      agency_id
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  },

  updateReservation: async (id: number, reservation: Partial<ConciergeReservation>): Promise<ConciergeReservation> => {
    const token = localStorage.getItem('token');
    const agency_id = getAgencyId();
    const response = await axios.put(`${API_BASE_URL}/concierge/reservations/${id}`, {
      ...reservation,
      agency_id
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  },

  deleteReservation: async (id: number): Promise<void> => {
    const token = localStorage.getItem('token');
    const agency_id = getAgencyId();
    await axios.delete(`${API_BASE_URL}/concierge/reservations/${id}`, {
      params: { agency_id },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  },

  getExpenses: async (propertyId: number, year: number, month: number): Promise<ExpensesResponse> => {
    const token = localStorage.getItem('token');
    const agency_id = getAgencyId();
    const response = await axios.get(`${API_BASE_URL}/concierge/expenses/${propertyId}/${year}/${month}`, {
      params: { agency_id },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  },

  createExpense: async (expense: ConciergeExpense): Promise<ConciergeExpense> => {
    const token = localStorage.getItem('token');
    const agency_id = getAgencyId();
    const response = await axios.post(`${API_BASE_URL}/concierge/expenses`, {
      ...expense,
      agency_id
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  },

  updateExpense: async (id: number, expense: Partial<ConciergeExpense>): Promise<ConciergeExpense> => {
    const token = localStorage.getItem('token');
    const agency_id = getAgencyId();
    const response = await axios.put(`${API_BASE_URL}/concierge/expenses/${id}`, {
      ...expense,
      agency_id
    }, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  },

  deleteExpense: async (id: number): Promise<void> => {
    const token = localStorage.getItem('token');
    const agency_id = getAgencyId();
    await axios.delete(`${API_BASE_URL}/concierge/expenses/${id}`, {
      params: { agency_id },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  },

  getBulletinData: async (propertyId: number, year: number, month: number): Promise<any> => {
    const token = localStorage.getItem('token');
    const agency_id = getAgencyId();
    const response = await axios.get(`${API_BASE_URL}/concierge/bulletin/${propertyId}/${year}/${month}`, {
      params: { agency_id },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  }
};
